<template>
  <div class="p-6 max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-500">
    <!-- Header -->
    <div class="flex flex-col md:flex-row items-center justify-between border-b border-border pb-6 gap-4">
      <div class="space-y-1 text-center md:text-left">
        <h1 class="text-3xl font-black tracking-tight text-foreground flex items-center gap-3">
          <GitCompare class="w-8 h-8 text-primary" /> Comparateur d'Investissements
        </h1>
        <p class="text-muted-foreground font-medium italic">Analysez et comparez les trajectoires de deux actifs.</p>
      </div>
      <Button variant="ghost" size="sm" class="text-muted-foreground font-bold hover:text-foreground" @click="router.back()">
        <ArrowLeft class="w-4 h-4 mr-2" /> Retour
      </Button>
    </div>

    <!-- Selectors -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card class="border-indigo-500/30 shadow-sm bg-card ring-1 ring-indigo-500/10 relative overflow-hidden">
        <div class="absolute right-0 top-0 w-32 h-32 bg-indigo-500/10 rounded-bl-[100px] pointer-events-none"></div>
        <CardHeader class="pb-2">
          <CardTitle class="text-sm font-bold text-indigo-500 uppercase tracking-widest flex items-center gap-2">
            <div class="w-2 h-2 rounded-full bg-indigo-500"></div> Investissement A
          </CardTitle>
        </CardHeader>
        <CardContent>
          <select v-model="selectedIdA" @change="fetchData" 
                  class="w-full bg-muted/50 border border-border rounded-xl p-3 text-sm font-bold text-foreground focus:ring-2 focus:ring-indigo-500/20 outline-none">
            <option :value="null">-- Sélectionner un investissement --</option>
            <option v-for="inv in allInvestments" :key="inv.id" :value="inv.id" :disabled="inv.id === selectedIdB">
              {{ inv.name }} {{ inv.isSimulation ? '(Simulation)' : '' }}
            </option>
          </select>
        </CardContent>
      </Card>

      <Card class="border-emerald-500/30 shadow-sm bg-card ring-1 ring-emerald-500/10 relative overflow-hidden">
        <div class="absolute right-0 top-0 w-32 h-32 bg-emerald-500/10 rounded-bl-[100px] pointer-events-none"></div>
        <CardHeader class="pb-2">
          <CardTitle class="text-sm font-bold text-emerald-500 uppercase tracking-widest flex items-center gap-2">
            <div class="w-2 h-2 rounded-full bg-emerald-500"></div> Investissement B
          </CardTitle>
        </CardHeader>
        <CardContent>
          <select v-model="selectedIdB" @change="fetchData" 
                  class="w-full bg-muted/50 border border-border rounded-xl p-3 text-sm font-bold text-foreground focus:ring-2 focus:ring-emerald-500/20 outline-none">
            <option :value="null">-- Sélectionner un investissement --</option>
            <option v-for="inv in allInvestments" :key="inv.id" :value="inv.id" :disabled="inv.id === selectedIdA">
              {{ inv.name }} {{ inv.isSimulation ? '(Simulation)' : '' }}
            </option>
          </select>
        </CardContent>
      </Card>
    </div>

    <!-- Chart & Stats -->
    <div v-if="selectedIdA && selectedIdB" class="grid grid-cols-1 lg:grid-cols-4 gap-6">
      
      <!-- Chart -->
      <Card class="lg:col-span-3 border-border shadow-sm bg-card relative min-h-[400px]">
        <div v-if="loading" class="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/50 backdrop-blur-sm rounded-xl">
          <RefreshCw class="w-8 h-8 text-primary animate-spin mb-4" />
          <span class="text-sm font-bold text-muted-foreground uppercase tracking-widest">Calcul des projections...</span>
        </div>
        <CardHeader class="flex flex-row items-center justify-between pb-2">
          <CardTitle class="text-lg font-bold">Projection Comparée</CardTitle>
          <div class="flex items-center gap-2">
            <span class="text-xs text-muted-foreground font-medium">Horizon :</span>
            <select v-model="projectionYears" @change="fetchData" class="bg-muted border border-border rounded-md px-2 py-1 text-sm font-bold outline-none cursor-pointer">
              <option :value="5">5 ans</option>
              <option :value="10">10 ans</option>
              <option :value="15">15 ans</option>
              <option :value="20">20 ans</option>
              <option :value="25">25 ans</option>
              <option :value="30">30 ans</option>
            </select>
          </div>
        </CardHeader>
        <CardContent class="h-[400px] w-full">
          <Line v-if="chartData && !loading" :data="chartData" :options="chartOptions" />
        </CardContent>
      </Card>

      <!-- Stats -->
      <div class="space-y-6">
        <!-- Stats A -->
        <Card class="border-indigo-500/30 shadow-sm bg-card">
          <CardHeader class="pb-2">
            <CardTitle class="text-sm font-bold text-indigo-500 truncate">{{ investmentA?.name }}</CardTitle>
          </CardHeader>
          <CardContent class="space-y-4">
            <div>
              <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Valorisation Actuelle</p>
              <p class="text-xl font-bold font-mono">{{ formatMoney(investmentA?.currentValue || 0) }}</p>
            </div>
            <div>
              <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Investi</p>
              <p class="text-base font-bold font-mono">{{ formatMoney(investmentA?.investedAmount || 0) }}</p>
            </div>
            <div>
              <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Rendement Annuel Espéré</p>
              <p class="text-base font-bold font-mono text-indigo-500">{{ investmentA?.expectedReturnRate || '0.00' }} %</p>
            </div>
            <div class="pt-2 border-t border-border">
              <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Proj. +5 ans</p>
              <p class="text-lg font-bold font-mono">{{ formatMoney(getProjectedValue(evolutionA, 5)) }}</p>
            </div>
            <div v-if="projectionYears > 5">
              <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Proj. +{{ projectionYears }} ans</p>
              <p class="text-lg font-bold font-mono">{{ formatMoney(getProjectedValue(evolutionA, projectionYears)) }}</p>
            </div>
          </CardContent>
        </Card>

        <!-- Stats B -->
        <Card class="border-emerald-500/30 shadow-sm bg-card">
          <CardHeader class="pb-2">
            <CardTitle class="text-sm font-bold text-emerald-500 truncate">{{ investmentB?.name }}</CardTitle>
          </CardHeader>
          <CardContent class="space-y-4">
            <div>
              <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Valorisation Actuelle</p>
              <p class="text-xl font-bold font-mono">{{ formatMoney(investmentB?.currentValue || 0) }}</p>
            </div>
            <div>
              <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Investi</p>
              <p class="text-base font-bold font-mono">{{ formatMoney(investmentB?.investedAmount || 0) }}</p>
            </div>
            <div>
              <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Rendement Annuel Espéré</p>
              <p class="text-base font-bold font-mono text-emerald-500">{{ investmentB?.expectedReturnRate || '0.00' }} %</p>
            </div>
            <div class="pt-2 border-t border-border">
              <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Proj. +5 ans</p>
              <p class="text-lg font-bold font-mono">{{ formatMoney(getProjectedValue(evolutionB, 5)) }}</p>
            </div>
            <div v-if="projectionYears > 5">
              <p class="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Proj. +{{ projectionYears }} ans</p>
              <p class="text-lg font-bold font-mono">{{ formatMoney(getProjectedValue(evolutionB, projectionYears)) }}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="py-20 flex flex-col items-center justify-center text-center opacity-50">
      <GitCompare class="w-16 h-16 text-muted-foreground mb-4" />
      <p class="text-lg font-bold">Sélectionnez deux investissements pour commencer la comparaison</p>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowLeft, GitCompare, RefreshCw } from 'lucide-vue-next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useGlobalState } from '../../../composables/useGlobalState'
import { Line } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const router = useRouter()
const { getAuthHeader } = useGlobalState()

const formatMoney = (val) => {
    const num = parseFloat(val)
    if (isNaN(num)) return '0 €'
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(num)
}

const allInvestments = ref([])
const selectedIdA = ref(null)
const selectedIdB = ref(null)
const projectionYears = ref(15)

const evolutionA = ref([])
const evolutionB = ref([])
const loading = ref(false)

const investmentA = computed(() => allInvestments.value.find(i => i.id === selectedIdA.value))
const investmentB = computed(() => allInvestments.value.find(i => i.id === selectedIdB.value))

const fetchInvestments = async () => {
    try {
        const res = await fetch('/api/investments', { headers: getAuthHeader() })
        if (res.ok) allInvestments.value = await res.json()
    } catch (e) { console.error(e) }
}

const fetchData = async () => {
    if (!selectedIdA.value || !selectedIdB.value) return
    loading.value = true
    try {
        const [resA, resB] = await Promise.all([
            fetch(`/api/investments/${selectedIdA.value}/evolution?pastYears=3&futureYears=${projectionYears.value}`, { headers: getAuthHeader() }),
            fetch(`/api/investments/${selectedIdB.value}/evolution?pastYears=3&futureYears=${projectionYears.value}`, { headers: getAuthHeader() })
        ])
        
        if (resA.ok && resB.ok) {
            evolutionA.value = await resA.json()
            evolutionB.value = await resB.json()
        }
    } catch (e) {
        console.error(e)
    } finally {
        loading.value = false
    }
}

const getProjectedValue = (evolutionData, yearsAhead) => {
    if (!evolutionData || evolutionData.length === 0) return 0
    const today = new Date()
    const targetYear = today.getFullYear() + yearsAhead
    const targetMonth = String(today.getMonth() + 1).padStart(2, '0')
    const targetDateStr = `${targetYear}-${targetMonth}`
    
    // Find closest point
    let closest = null
    for (const pt of evolutionData) {
        if (pt.date >= targetDateStr) {
            closest = pt
            break
        }
    }
    if (!closest) closest = evolutionData[evolutionData.length - 1]
    return closest ? closest.total : 0
}

const chartData = computed(() => {
    if (!evolutionA.value.length && !evolutionB.value.length) return null

    // Merge dates
    const dateSet = new Set()
    evolutionA.value.forEach(d => dateSet.add(d.date))
    evolutionB.value.forEach(d => dateSet.add(d.date))
    const allDates = Array.from(dateSet).sort()

    const labels = allDates.map(d => {
        const parts = d.split('-')
        return `${parts[1]}/${parts[0]}`
    })

    // Map data points
    const dataA = allDates.map(date => {
        const pt = evolutionA.value.find(d => d.date === date)
        return pt ? pt.total : null // could interpolate if needed, but the simulation gives monthly points
    })

    const dataB = allDates.map(date => {
        const pt = evolutionB.value.find(d => d.date === date)
        return pt ? pt.total : null
    })

    return {
        labels,
        datasets: [
            {
                label: investmentA.value?.name || 'A',
                data: dataA,
                borderColor: '#6366f1', // indigo-500
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                borderWidth: 3,
                pointRadius: 0,
                pointHoverRadius: 5,
                tension: 0.1,
                fill: true
            },
            {
                label: investmentB.value?.name || 'B',
                data: dataB,
                borderColor: '#10b981', // emerald-500
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                borderWidth: 3,
                pointRadius: 0,
                pointHoverRadius: 5,
                tension: 0.1,
                fill: true
            }
        ]
    }
})

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
        mode: 'index',
        intersect: false,
    },
    plugins: {
        legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8, font: { weight: 'bold' } } },
        tooltip: {
            backgroundColor: 'rgba(24, 24, 27, 0.95)',
            titleFont: { size: 13, family: 'Inter', weight: 'bold' },
            bodyFont: { size: 14, family: 'Inter', weight: 'bold' },
            padding: 12,
            cornerRadius: 8,
            callbacks: {
                label: (context) => {
                    let label = context.dataset.label || '';
                    if (label) label += ': ';
                    if (context.parsed.y !== null) {
                        label += new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(context.parsed.y);
                    }
                    return label;
                }
            }
        }
    },
    scales: {
        y: {
            beginAtZero: true,
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: {
                callback: (value) => new Intl.NumberFormat('fr-FR', { notation: 'compact', style: 'currency', currency: 'EUR', maximumFractionDigits: 1 }).format(value)
            }
        },
        x: { grid: { display: false } }
    }
}

onMounted(() => {
    fetchInvestments()
})
</script>
